import { useState } from "react";

import {
  PlanningScope,
  PlanningType,
  type FullAirHandlerUnit,
  type Planning,
  type PutPlanningBody,
} from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Button, Heading } from "@eisox/design-system";
import { CopyIcon, PlusIcon } from "@eisox/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { airHandlerUnitApi, planningApi } from "~/api";
import { Help } from "~/components/help";
import { Tooltip } from "~/components/tooltip";
import {
  CopyPlanningDialog,
  copyToPutPlanningBody,
  mergeTimeslot,
  OptionsDropdown,
  PlanningTypeKey,
  UpdatePlanningDialog,
  Week,
  type CopySchemaType,
} from "~/features/planning";
import { m } from "~/paraglide/messages";

import { airHandlerUnitsToPlaces } from "../helpers";

interface WeeklyPlanningProps {
  buildingId: string;
  airHandlerUnit: FullAirHandlerUnit[];
  airHandlerUnits: FullAirHandlerUnit[];
  plannings: Planning[];
}

export const WeeklyPlanning: React.FC<WeeklyPlanningProps> = ({
  buildingId,
  airHandlerUnit,
  airHandlerUnits,
  plannings,
}) => {
  const [displayUserDefinedPlanning, setDisplayUserDefinedPlanning] = useState(true);

  const queryClient = useQueryClient();
  const groupIds = airHandlerUnit.map(ahu => ahu.id);

  const { mutateAsync } = useMutation({
    mutationFn: (body: PutPlanningBody) => planningApi.update(buildingId, body),
    onSuccess: async (_, request) => {
      await Promise.all(
        request.parentIds.map(airHandlerUnitId =>
          queryClient.invalidateQueries({ queryKey: airHandlerUnitApi.queries.plannings(airHandlerUnitId).queryKey }),
        ),
      );
    },
  });

  const generatedPlannings = plannings.filter(planning => !planning.userDefined);
  const userDefinedPlannings = plannings.filter(planning => planning.userDefined);
  const mergedPlannings = displayUserDefinedPlanning ? mergeTimeslot(plannings) : generatedPlannings;

  const firstDayOfWeek = dayjs.localeData().firstDayOfWeek();
  const daysOfWeek = [...Array(7).keys()];
  const reorderedDays = [...daysOfWeek.slice(firstDayOfWeek), ...daysOfWeek.slice(0, firstDayOfWeek)];
  const getEnglishDayName = (dayNumber: number) => dayjs().locale("en").day(dayNumber).format("dddd").toLowerCase();

  const timeslotsByDay = reorderedDays.reduce(
    (acc, dayNumber) => {
      const englishDayName = getEnglishDayName(dayNumber);
      acc[dayNumber] = mergedPlannings.filter(planning => planning.day === englishDayName);
      return acc;
    },
    {} as Record<number, Planning[]>,
  );

  const types: PlanningType[] = [PlanningType.OCCUPATION, PlanningType.INOCCUPATION, PlanningType.STOP];
  const source = airHandlerUnitsToPlaces(airHandlerUnit)[0];
  const places = airHandlerUnitsToPlaces(airHandlerUnits);

  const handleChangePlanning = async (plannings: Planning[]) => {
    await mutateAsync({ scope: PlanningScope.AIR_HANDLER_UNIT, parentIds: groupIds, plannings });
  };

  const handleCopyPlanning = async (data: CopySchemaType) => {
    await mutateAsync({
      ...copyToPutPlanningBody(data, { parentIds: groupIds, userDefinedPlannings }, places),
      scope: PlanningScope.AIR_HANDLER_UNIT,
    });
  };

  return (
    <div className="max-w-115 flex flex-col">
      <div className="mb-5 flex items-center justify-between">
        <Heading>{m.weekly_planning()}</Heading>
        <div className="gap-2.5 pl-20 flex items-center [&_svg]:text-primary-blue">
          <UpdatePlanningDialog
            userDefinedPlannings={userDefinedPlannings}
            types={types}
            onValueChange={handleChangePlanning}
          >
            <Button>
              <PlusIcon />
            </Button>
          </UpdatePlanningDialog>
          {source && (
            <CopyPlanningDialog
              resource={"airHandlerUnit"}
              source={source}
              places={places}
              onCopy={data => {
                void handleCopyPlanning(data);
              }}
            >
              <Button>
                <CopyIcon />
              </Button>
            </CopyPlanningDialog>
          )}
          <OptionsDropdown
            displayUserDefinedPlanning={displayUserDefinedPlanning}
            onDisplayUserDefinedPlanningChange={setDisplayUserDefinedPlanning}
          />
        </div>
      </div>
      <Week.Root>
        {reorderedDays.map(day => (
          <Week.Day key={day} day={day as 0 | 1 | 2 | 3 | 4 | 5 | 6} className="min-h-125">
            {(timeslotsByDay[day] ?? []).map((timeslot, index) =>
              timeslot.userDefined ? (
                <UpdatePlanningDialog
                  key={index}
                  userDefinedPlannings={userDefinedPlannings}
                  types={types}
                  value={timeslot}
                  onValueChange={handleChangePlanning}
                >
                  <Week.Timeslot start={timeslot.hourStart} end={timeslot.hourEnd} type={timeslot.type} userDefined />
                </UpdatePlanningDialog>
              ) : (
                <Week.Timeslot key={index} start={timeslot.hourStart} end={timeslot.hourEnd} type={timeslot.type} />
              ),
            )}
          </Week.Day>
        ))}
      </Week.Root>
      <div className="mt-5 flex flex-wrap justify-between">
        <PlanningTypeKey type={PlanningType.OCCUPATION} />
        <PlanningTypeKey type={PlanningType.INOCCUPATION} />
        <div className="gap-1.25 flex items-center">
          <PlanningTypeKey type={PlanningType.STOP} />
          <Tooltip content={m.air_handler_unit_planning_stop_help()}>
            <Help />
          </Tooltip>
        </div>
        <PlanningTypeKey type="userDefined" />
      </div>
    </div>
  );
};
